import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation, Trans } from 'react-i18next';
import { apiClient } from '@/lib/api-client';
import { formatUZS } from '@/lib/format';
import { useApp } from '@/context/AppContext';
import { useMerchantId } from '@/hooks/useMerchantId';
import { Search, Plus, Minus, Trash2, CreditCard, Banknote, Wallet, ShoppingCart, User, X, AlertTriangle, Shield, Save, FolderOpen, WifiOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PaymentMethod } from '@/types';
import CartSessionTabs from '@/components/pos/CartSessionTabs';
import DraftsDialog from '@/components/pos/DraftsDialog';
import CartResizeHandle from '@/components/pos/CartResizeHandle';
import QuickProducts from '@/components/pos/QuickProducts';
import { useOfflineQueue } from '@/hooks/use-offline-queue';
import { useCategories } from '@/hooks/use-categories';
import { cacheProducts, getProductsCache } from '@/lib/offline-queue';
import { toast } from 'sonner';

// Sentinel value of the "all categories" filter (displayed via i18n)
const ALL_CATEGORY = 'Barchasi';
const MIN_CART_WIDTH = 320;
const MAX_CART_WIDTH = 720;
const DEFAULT_CART_WIDTH = 480;

function clampWidth(width: number) {
  return Math.max(MIN_CART_WIDTH, Math.min(MAX_CART_WIDTH, Math.round(width)));
}

function getCartWidthKey(merchantId: string | null, userId: string | undefined) {
  return `hisob-pos-cart-width:${merchantId ?? 'none'}:${userId ?? 'guest'}`;
}

export default function POSHome() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(ALL_CATEGORY);
  const [draftsOpen, setDraftsOpen] = useState(false);
  const [cartWidth, setCartWidth] = useState(DEFAULT_CART_WIDTH);
  const {
    cart, addToCart, updateQuantity, removeFromCart, setItemUnit, cartTotal, clearCart,
    murabahaCustomer, clearMurabahaCustomer, remainingLimit,
    paymentMethod, setPaymentMethod, drafts, saveAsDraft,
  } = useApp();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { merchantId } = useMerchantId();
  const { data: dbCategories = [] } = useCategories();
  const { isOnline, queuedCount } = useOfflineQueue();
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!merchantId || !user?.id) return;
    const stored = localStorage.getItem(getCartWidthKey(merchantId, user.id));
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed)) {
        setCartWidth(clampWidth(parsed));
      }
    }
  }, [merchantId, user?.id]);

  const handleResize = useCallback((width: number) => {
    setCartWidth(clampWidth(width));
  }, []);

  const handleResizeEnd = useCallback((width: number) => {
    const clamped = clampWidth(width);
    setCartWidth(clamped);
    if (merchantId && user?.id) {
      localStorage.setItem(getCartWidthKey(merchantId, user.id), String(clamped));
    }
  }, [merchantId, user?.id]);


  const { data: products = [], isLoading } = useQuery({
    queryKey: ['products-with-prices', merchantId],
    queryFn: async () => {
      try {
        // IndexProductController already filters is_active=true server-side.
        const { data, error } = await apiClient.GET('/products');
        if (error) throw error;
        const list = data?.data ?? [];
        cacheProducts(merchantId, list).catch(() => {});
        return list;
      } catch (err) {
        // Oflayn yoki xato — so'nggi keshlangan ro'yxat bilan davom etamiz
        const cached = await getProductsCache(merchantId);
        if (cached && cached.length > 0) return cached;
        throw err;
      }
    },
    enabled: !!merchantId,
  });

  const categories: string[] = [ALL_CATEGORY, ...dbCategories.map((c: any) => c.name)];
  const filtered = products.filter((p: any) => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase()) || p.barcode.includes(search) || p.sku.toLowerCase().includes(search.toLowerCase());
    const matchCat = selectedCategory === ALL_CATEGORY || p.category === selectedCategory;
    return matchSearch && matchCat;
  });

  const hasMurabaha = cart.some(c => c.product.is_murabaha_eligible);
  const isLimitMode = !!murabahaCustomer;
  const limitExceeded = isLimitMode && remainingLimit !== null && remainingLimit < 0;

  const handleAddToCart = (product: any) => {
    const salePrice = Number(product.sale_price) || 0;
    const price = salePrice > 0 ? salePrice : (Number(product.price) || 0);

    // In limit mode, check if adding would exceed limit
    if (isLimitMode && remainingLimit !== null) {
      const existingItem = cart.find(c => c.product.id === product.id);
      const newItemTotal = existingItem ? price : price; // adding 1 more
      if ((remainingLimit - newItemTotal) < 0) {
        // Still allow adding but will show warning
      }
    }
    
    addToCart(product as any, price);
  };

  const handleCheckout = () => {
    if (isLimitMode) {
      // For murabaha customer, go directly to murabaha checkout
      navigate('/pos/murabaha');
    } else {
      navigate('/pos/checkout', { state: { paymentMethod } });
    }
  };

  // Tezkor klaviatura: F2 — qidiruv, F9 — to'lash, Esc — qidiruvni tozalash
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F2') {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      } else if (e.key === 'F9') {
        e.preventDefault();
        if (cart.length > 0) handleCheckout();
      } else if (e.key === 'Escape' && document.activeElement === searchRef.current && search) {
        setSearch('');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  // Skaner: Enter bosilganda barcode/SKU bo'yicha aniq mos mahsulot savatchaga tushadi
  const handleScanEnter = () => {
    const code = search.trim().toLowerCase();
    if (!code) return;
    const exact = products.filter((p: any) => p.barcode?.toLowerCase() === code || p.sku?.toLowerCase() === code);
    const target = exact.length === 1
      ? exact[0]
      : exact.length === 0 && filtered.length === 1 ? filtered[0] : null;
    if (target) {
      handleAddToCart(target);
      setSearch('');
    }
  };

  const handleCancelMurabaha = () => {
    clearMurabahaCustomer();
    clearCart();
  };

  return (
    <div className="flex flex-col h-screen bg-pos-surface">
      {/* Header */}
      <header className="flex items-center justify-between h-14 px-4 bg-card border-b border-border shrink-0">
        <div className="flex items-center gap-3">
          <Link to="/dashboard" className="p-2 rounded-lg hover:bg-accent text-muted-foreground">
            <ArrowLeft size={18} />
          </Link>
          <span className="font-semibold text-foreground text-sm">{t('pos_home.title')}</span>
        </div>
        <div className="flex items-center gap-3">
          {(!isOnline || queuedCount > 0) && (
            <span
              className="flex items-center gap-1 px-2 py-1 rounded-full bg-warning/10 text-warning text-[10px] font-medium"
              title={!isOnline ? t('pos_home.offline_title') : t('pos_home.queued_title')}
            >
              <WifiOff size={10} />
              {!isOnline ? t('pos_home.offline') : t('pos_home.queued_n', { n: queuedCount })}
            </span>
          )}
          <span className="text-xs text-muted-foreground">{user?.full_name}</span>
        </div>
      </header>

      {/* Murabaha customer banner */}
      {isLimitMode && murabahaCustomer && (
        <div className="px-4 py-2.5 bg-primary/5 border-b border-primary/20 flex items-center gap-3 shrink-0">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <User size={16} className="text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-foreground truncate">{murabahaCustomer.full_name}</span>
              <Badge variant="secondary" className="text-[10px] shrink-0">
                <Shield size={10} className="mr-0.5" /> Murabaha
              </Badge>
            </div>
            <div className="flex items-center gap-3 text-xs mt-0.5">
              <span className="text-muted-foreground">{murabahaCustomer.phone}</span>
              <span className="text-muted-foreground">•</span>
              <span className="text-muted-foreground">{t('pos_home.limit')} <span className="font-semibold text-foreground">{formatUZS(murabahaCustomer.murabaha_limit)}</span></span>
              <span className="text-muted-foreground">•</span>
              <span className={`font-semibold ${limitExceeded ? 'text-destructive' : 'text-success'}`}>
                {t('pos_home.remainder', { amount: formatUZS(remainingLimit ?? 0) })}
              </span>
            </div>
          </div>
          <button
            onClick={handleCancelMurabaha}
            className="p-1.5 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors shrink-0"
            title={t('pos_home.cancel_murabaha')}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Offline / navbat banneri */}
      {(!isOnline || queuedCount > 0) && (
        <div className="px-4 py-2 bg-warning/10 border-b border-warning/20 flex items-center gap-2 text-xs text-warning shrink-0">
          <WifiOff size={14} />
          <span>
            {!isOnline
              ? t('pos_home.offline_banner', { queued: queuedCount > 0 ? t('pos_home.offline_banner_queued', { n: queuedCount }) : '' })
              : t('pos_home.queued_banner', { n: queuedCount })}
          </span>
        </div>
      )}

      {/* Limit exceeded warning */}
      {limitExceeded && (
        <div className="px-4 py-2 bg-destructive/10 border-b border-destructive/20 flex items-center gap-2 text-xs text-destructive shrink-0">
          <AlertTriangle size={14} />
          <span><Trans i18nKey="pos_home.limit_exceeded_warn" values={{ amount: formatUZS(Math.abs(remainingLimit ?? 0)) }} components={{ b: <strong /> }} /></span>
        </div>
      )}

      {/* Main content: products left, cart right */}
      <div className="flex flex-1 overflow-hidden">
        {/* Products panel */}
        <div className="flex-1 overflow-auto p-4 animate-fade-in">
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1 max-w-md">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                ref={searchRef}
                placeholder={t('pos_home.search_placeholder')}
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleScanEnter(); } }}
                className="pl-9 h-10"
                autoFocus
              />
              <p className="mt-1 text-[11px] text-muted-foreground">{t('pos_home.shortcuts')}</p>
            </div>
          </div>

          <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
            {categories.map(cat => (
              <button key={cat} onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors ${selectedCategory === cat ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground border border-border hover:bg-accent'}`}>
                {cat === ALL_CATEGORY ? t('pos_home.all') : cat}
              </button>
            ))}
          </div>

          {!isLoading && (
            <QuickProducts products={products} onAdd={handleAddToCart} merchantId={merchantId} />
          )}

          {isLoading ? (
            <p className="text-center text-muted-foreground p-8">{t('pos_home.loading')}</p>
          ) : (
            <div className="pos-grid">
              {filtered.map((product: any) => (
                <button key={product.id} onClick={() => handleAddToCart(product)}
                  className="bg-card border border-border rounded-xl p-3 text-left hover:border-primary/30 hover:shadow-sm transition-all group">
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-xs text-muted-foreground">{product.sku}</span>
                    {product.is_murabaha_eligible && <span className="text-[10px] px-1.5 py-0.5 rounded bg-success/10 text-success">Murabaha</span>}
                  </div>
                  <p className="text-sm font-medium text-foreground mb-1 line-clamp-2">{product.name}</p>
                  <p className="text-xs text-muted-foreground mb-2">{product.category}</p>
                  {product.installment && (
                    <p className="text-sm font-bold text-foreground tabular-nums mb-2">
                      {t('pos_home.per_month', { amount: formatUZS(Math.round(product.installment.monthly_payment)) })}
                    </p>
                  )}
                  <div className="flex items-center justify-between">
                    {Number(product.sale_price) > 0 ? (
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-warning">{formatUZS(product.sale_price)}</span>
                        <span className="text-[10px] text-muted-foreground line-through">{formatUZS(Number(product.price) || 0)}</span>
                      </div>
                    ) : (
                      <span className="text-sm font-semibold text-foreground">{formatUZS(Number(product.price) || 0)}</span>
                    )}
                    <span className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Plus size={14} />
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Cart sidebar */}
        <div
          id="pos-cart-sidebar"
          style={{ width: cartWidth }}
          className="relative border-l border-border bg-card flex flex-col shrink-0"
        >
          <CartResizeHandle onResize={handleResize} onResizeEnd={handleResizeEnd} />
          <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
            <ShoppingCart size={16} className="text-primary" />
            <span className="text-sm font-semibold text-foreground">{t('pos_home.cart')}</span>
            <span className="ml-auto text-xs text-muted-foreground">{t('pos_common.count_pcs', { n: cart.length })}</span>
            <button
              onClick={() => setDraftsOpen(true)}
              className="relative flex items-center gap-1 px-2 py-1 rounded-md text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
              title={t('pos_home.drafts_title')}
            >
              <FolderOpen size={13} /> {t('pos_home.drafts')}
              {drafts.length > 0 && (
                <span className="ml-0.5 px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold">{drafts.length}</span>
              )}
            </button>
          </div>

          <CartSessionTabs />

          {cart.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
              {isLimitMode ? t('pos_home.choose_in_limit') : t('pos_cart.empty')}
            </div>

          ) : (
            <>
              {/* Cart items */}
              <div className="flex-1 overflow-auto p-3 space-y-2">
                {cart.map(item => (
                  <div key={item.product.id} className="bg-muted/50 rounded-lg p-3">
                    <div className="flex items-start justify-between mb-2">
                      <p className="text-sm font-medium text-foreground line-clamp-1 flex-1 mr-2">{item.product.name}</p>
                      <button onClick={() => removeFromCart(item.product.id)} className="text-destructive hover:bg-destructive/10 p-1 rounded shrink-0">
                        <Trash2 size={12} />
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => updateQuantity(item.product.id, item.quantity - 1)} className="w-6 h-6 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-accent">
                          <Minus size={10} />
                        </button>
                        <span className="text-xs font-medium w-6 text-center">{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.product.id, item.quantity + 1)} className="w-6 h-6 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-accent">
                          <Plus size={10} />
                        </button>
                      </div>
                      <span className="text-sm font-semibold text-foreground">{formatUZS(item.total)}</span>
                    </div>
                    {(item.product.allowed_units?.length ?? 0) > 1 ? (
                      <select
                        value={item.selected_unit || item.product.unit}
                        onChange={e => setItemUnit(item.product.id, e.target.value)}
                        className="mt-2 w-full text-xs bg-background border border-border rounded-md px-2 py-1 text-foreground"
                      >
                        {item.product.allowed_units!.map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                    ) : (
                      <p className="mt-1 text-[11px] text-muted-foreground">{item.selected_unit || item.product.unit}</p>
                    )}
                  </div>
                ))}
              </div>

              {/* Payment & total */}
              <div className="border-t border-border p-4 space-y-3">
                {/* Limit progress bar (murabaha mode) */}
                {isLimitMode && murabahaCustomer && (
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">{t('pos_home.limit_spent')}</span>
                      <span className={`font-medium ${limitExceeded ? 'text-destructive' : 'text-foreground'}`}>
                        {formatUZS(cartTotal)} / {formatUZS(murabahaCustomer.murabaha_limit)}
                      </span>
                    </div>
                    <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${limitExceeded ? 'bg-destructive' : 'bg-primary'}`}
                        style={{ width: `${Math.min(100, (cartTotal / murabahaCustomer.murabaha_limit) * 100)}%` }}
                      />
                    </div>
                    <p className={`text-xs ${limitExceeded ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
                      {limitExceeded
                        ? t('pos_home.over_limit', { amount: formatUZS(Math.abs(remainingLimit ?? 0)) })
                        : t('pos_home.remainder', { amount: formatUZS(remainingLimit ?? 0) })}
                    </p>
                  </div>
                )}

                {/* Payment method selector (only for non-murabaha) */}
                {!isLimitMode && (
                  <div className="flex gap-2">
                    {([
                      { method: 'cash' as PaymentMethod, icon: Banknote, label: t('pos_pay.cash') },
                      { method: 'card' as PaymentMethod, icon: CreditCard, label: t('pos_pay.card') },
                      { method: 'hisob_wallet' as PaymentMethod, icon: Wallet, label: t('pos_pay.wallet') },
                    ]).map(pm => (
                      <button
                        key={pm.method}
                        onClick={() => setPaymentMethod(pm.method)}
                        className={`flex items-center gap-1.5 flex-1 justify-center px-2 py-2 rounded-lg text-xs transition-colors ${
                          paymentMethod === pm.method ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground hover:bg-accent'
                        }`}
                      >
                        <pm.icon size={14} /> {pm.label}
                      </button>
                    ))}
                  </div>
                )}

                {/* Murabaha payment indicator */}
                {isLimitMode && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/10 text-primary text-xs">
                    <Shield size={14} />
                    <span className="font-medium">{t('pos_home.nasiya_murabaha')}</span>
                  </div>
                )}

                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">{t('pos_cart.total')}</span>
                  <span className="text-lg font-bold text-foreground">{formatUZS(cartTotal)}</span>
                </div>

                {isLimitMode ? (
                  <Button
                    onClick={handleCheckout}
                    className="w-full h-12 text-base"
                    disabled={limitExceeded || !isOnline}
                    title={!isOnline ? t('pos_home.nasiya_needs_internet') : undefined}
                  >
                    {t('pos_home.nasiya_checkout')}
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={() => navigate('/pos/murabaha')}
                      className="w-full h-12 text-base"
                      disabled={!hasMurabaha || !isOnline}
                      title={!isOnline ? t('pos_home.nasiya_needs_internet') : (hasMurabaha ? undefined : t('pos_home.no_eligible'))}
                    >
                      <Shield size={16} className="mr-2" /> {t('pos_pay.nasiya')}
                    </Button>
                    <Button variant="outline" onClick={handleCheckout} className="w-full h-11">
                      {t('pos_cart.pay')}
                    </Button>
                  </>
                )}

                <Button
                  variant="ghost"
                  onClick={() => {
                    const name = window.prompt(t('pos_home.draft_name_prompt'), murabahaCustomer?.full_name || '');
                    if (name === null) return;
                    saveAsDraft(name || undefined);
                    toast.success(t('pos_home.draft_saved'));
                  }}
                  className="w-full h-9 text-xs text-muted-foreground"
                >
                  <Save size={13} className="mr-1.5" /> {t('pos_home.save_draft')}
                </Button>

                <button onClick={() => { clearCart(); if (isLimitMode) clearMurabahaCustomer(); }} className="text-xs text-destructive hover:underline w-full text-center">
                  {isLimitMode ? t('pos_home.cancel') : t('pos_home.clear')}
                </button>

              </div>
            </>
          )}
        </div>
      </div>

      <DraftsDialog open={draftsOpen} onOpenChange={setDraftsOpen} />
    </div>

  );
}
